import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, GitCompare, Zap, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useCopyScoring } from '../hooks/useCopyScoring';
import { AB_ARENA_EXERCISES } from '../data/copywritingExercises';
import CopyEditor from '../components/CopyEditor';
import CopyScoreDisplay from '../components/CopyScoreDisplay';

const ACCENT = '#7c3aed';

const ABArena = () => {
  usePageTitle('The A/B Arena | Castle Atlas');
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { scoreExercise, loading, error, clearError } = useCopyScoring();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  const [currentIdx, setCurrentIdx] = useState(0);

  // pick-justify state
  const [pick, setPick] = useState(null); // 'A' | 'B'
  const [justification, setJustification] = useState('');

  // generate-defend state
  const [variant1, setVariant1] = useState('');
  const [variant2, setVariant2] = useState('');
  const [variant3, setVariant3] = useState('');
  const [defendChoice, setDefendChoice] = useState(null); // 1 | 2 | 3
  const [defense, setDefense] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(null);

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (exerciseId) {
      const idx = AB_ARENA_EXERCISES.findIndex(e => e.id === exerciseId);
      if (idx >= 0) setCurrentIdx(idx);
    }
  }, [exerciseId]);

  const exercise = AB_ARENA_EXERCISES[currentIdx];
  const isPickJustify = exercise?.mode === 'pick-justify';

  const canSubmit = isPickJustify
    ? Boolean(pick) && justification.trim().length > 0
    : variant1.trim() && variant2.trim() && variant3.trim() && Boolean(defendChoice) && defense.trim().length > 0;

  const handleSubmit = useCallback(async () => {
    if (submitted || !canSubmit) return;
    setSubmitted(true);

    try {
      const payload = isPickJustify
        ? {
            exerciseId: exercise.id,
            moduleId: 'ab-arena',
            mode: exercise.mode,
            userChoice: pick,
            userText: justification.trim(),
          }
        : {
            exerciseId: exercise.id,
            moduleId: 'ab-arena',
            mode: exercise.mode,
            variants: [variant1.trim(), variant2.trim(), variant3.trim()],
            defendedVariant: defendChoice,
            userText: defense.trim(),
          };
      const res = await scoreExercise(payload);
      setResult(res);
      setScore(res.scores?.overall ?? 0);
    } catch (err) {
      // unlock so the student can retry; the hook holds the message shown under the button
      console.error(err);
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, canSubmit, isPickJustify, pick, justification, variant1, variant2, variant3, defendChoice, defense, exercise, scoreExercise]);

  const nextExercise = () => {
    if (currentIdx < AB_ARENA_EXERCISES.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setPick(null); setJustification('');
      setVariant1(''); setVariant2(''); setVariant3(''); setDefendChoice(null); setDefense('');
      setSubmitted(false); setResult(null); setScore(null); clearError();
      navigate(`/skills/copywriting/ab-arena/${AB_ARENA_EXERCISES[currentIdx + 1].id}`, { replace: true });
    }
  };

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card: isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#78716c' : '#a8a29e',
    sub: isDark ? '#57534e' : '#b4aca6',
    winnerBg: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
    winnerBorder: 'rgba(34,197,94,0.3)',
    loserBg: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
    loserBorder: 'rgba(239,68,68,0.3)',
  };

  const AxisPill = ({ label, muted }) => (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99,
      background: muted ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'rgba(124,58,237,0.10)',
      color: muted ? c.muted : ACCENT, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{label}</span>
  );

  if (!exercise) return null;

  return (
    <div style={{ maxWidth: '100%', paddingBottom: 80, background: c.pageBg, minHeight: '100vh' }}>
    <div className="rail-clearance">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);} }
        @keyframes popIn { from{opacity:0;transform:scale(0.9);}to{opacity:1;transform:scale(1);} }
        .anim-fade { animation: fadeUp 0.3s ease-out both; }
        .anim-pop { animation: popIn 0.4s ease-out both; }
      `}</style>

      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: isDark ? 'rgba(14,13,12,0.85)' : 'rgba(250,249,247,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/skills/copywriting')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8,
        }}>
          <ArrowLeft size={18} color={c.head} />
        </button>
        <GitCompare size={16} color={ACCENT} />
        <span style={{ fontSize: 13, fontWeight: 800, color: c.head, flex: 1 }}>The A/B Arena</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.muted }}>
          {currentIdx + 1} / {AB_ARENA_EXERCISES.length}
        </span>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '16px 16px 40px' : '24px 24px 60px' }}>
        {/* Brief Card */}
        <div className="anim-fade" style={{
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: 20, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {exercise.sourceModule && <AxisPill label={`from ${exercise.sourceModule.replace('-', ' ')}`} muted />}
            <AxisPill label={exercise.primaryAxis} />
            {exercise.secondaryAxes?.map(a => <AxisPill key={a} label={a} muted />)}
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: c.head, marginBottom: 10 }}>{exercise.title}</h2>
          <p style={{ fontSize: 13, color: c.sub, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {exercise.briefText}
          </p>
        </div>

        {isPickJustify ? (
          <>
            {/* Two given variants */}
            <div className="anim-fade" style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 20,
            }}>
              {[['A', exercise.optionA], ['B', exercise.optionB]].map(([label, text]) => (
                <button key={label} onClick={() => setPick(label)} disabled={submitted} style={{
                  textAlign: 'left', padding: '16px 18px', borderRadius: 16, cursor: submitted ? 'default' : 'pointer',
                  background: pick === label ? 'rgba(124,58,237,0.10)' : c.card,
                  border: `1.5px solid ${pick === label ? ACCENT : c.border}`,
                  transition: 'all 0.15s ease',
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 7, marginBottom: 10,
                    background: pick === label ? ACCENT : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                    color: pick === label ? '#fff' : c.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800,
                  }}>{label}</div>
                  <div style={{ fontSize: 13, color: c.head, lineHeight: 1.5 }}>{text}</div>
                </button>
              ))}
            </div>

            <div className="anim-fade" style={{ marginBottom: 20 }}>
              <CopyEditor
                key={`${exercise.id}:justification`}
                draftKey={`${exercise.id}:justification`}
                value={justification}
                onChange={setJustification}
                placeholder="Name the principle, then reconcile it with what the losing option does better..."
                label="Your justification"
                rows={4}
                isDark={isDark}
                disabled={submitted}
              />
            </div>
          </>
        ) : (
          <>
            {exercise.source_content && (
              <div className="anim-fade" style={{
                background: 'rgba(124,58,237,0.05)', border: `1.5px solid ${isDark ? '#4c3580' : '#c4b5fd'}`,
                borderRadius: 16, padding: '14px 18px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  Source
                </div>
                <div style={{ fontSize: 13, color: c.head, lineHeight: 1.6 }}>{exercise.source_content}</div>
              </div>
            )}

            <div className="anim-fade" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
              {[1, 2, 3].map(n => (
                <div key={n} style={{
                  border: `1.5px solid ${defendChoice === n ? ACCENT : c.border}`,
                  borderRadius: 14, padding: '4px 4px 8px',
                  background: defendChoice === n ? 'rgba(124,58,237,0.05)' : 'transparent',
                }}>
                  <CopyEditor
                    key={`${exercise.id}:variant-${n}`}
                    draftKey={`${exercise.id}:variant-${n}`}
                    value={n === 1 ? variant1 : n === 2 ? variant2 : variant3}
                    onChange={n === 1 ? setVariant1 : n === 2 ? setVariant2 : setVariant3}
                    placeholder={`Variant ${n}...`}
                    label={`Variant ${n}`}
                    rows={2}
                    isDark={isDark}
                    disabled={submitted}
                  />
                  <button
                    onClick={() => setDefendChoice(n)}
                    disabled={submitted}
                    style={{
                      marginTop: 6, marginLeft: 4, padding: '6px 12px', borderRadius: 8,
                      background: defendChoice === n ? ACCENT : 'transparent',
                      color: defendChoice === n ? '#fff' : c.muted,
                      border: `1px solid ${defendChoice === n ? ACCENT : c.border}`,
                      fontSize: 11, fontWeight: 700, cursor: submitted ? 'default' : 'pointer',
                    }}
                  >
                    {defendChoice === n ? "This one — I'll defend it" : "I'd defend this one"}
                  </button>
                </div>
              ))}
            </div>

            <div className="anim-fade" style={{ marginBottom: 20 }}>
              <CopyEditor
                key={`${exercise.id}:defense`}
                draftKey={`${exercise.id}:defense`}
                value={defense}
                onChange={setDefense}
                placeholder="Why this variant, and what does each of the other two sacrifice?"
                label="Your defense"
                rows={4}
                isDark={isDark}
                disabled={submitted}
              />
            </div>
          </>
        )}

        {/* Submit */}
        {!submitted && (
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            style={{
              width: '100%', padding: '14px 24px', borderRadius: 12,
              background: !canSubmit || loading ? (isDark ? '#2d2825' : '#e8e4df') : ACCENT,
              color: !canSubmit || loading ? c.muted : '#fff',
              fontSize: 14, fontWeight: 700, border: 'none', cursor: !canSubmit || loading ? 'not-allowed' : 'pointer',
              boxShadow: !canSubmit || loading ? 'none' : `0 4px 20px -4px rgba(124,58,237,0.45)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Zap size={15} />
            {loading ? 'Judging...' : isPickJustify ? 'Submit Pick' : 'Submit & Defend'}
          </button>
        )}
        {error && !submitted && (
          <p role="alert" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: '#ef4444', lineHeight: 1.5 }}>
            {error} Your answer is still here; try again.
          </p>
        )}

        {/* Results */}
        {submitted && result && (
          <div className="anim-fade" style={{ marginTop: 24 }}>
            {isPickJustify && typeof result.pickCorrect === 'boolean' && (
              <div className="anim-pop" style={{
                marginBottom: 20, padding: '16px 20px', borderRadius: 16,
                background: result.pickCorrect ? c.winnerBg : c.loserBg,
                border: `1.5px solid ${result.pickCorrect ? c.winnerBorder : c.loserBorder}`,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                {result.pickCorrect
                  ? <CheckCircle2 size={22} color="#22c55e" />
                  : <XCircle size={22} color="#ef4444" />}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: result.pickCorrect ? '#22c55e' : '#ef4444' }}>
                    {result.pickCorrect ? 'You called it!' : 'Not this time'}
                  </div>
                  {!result.pickCorrect && result.correctChoice && (
                    <div style={{ fontSize: 12, color: c.sub, marginTop: 2 }}>
                      You picked {pick}, the stronger option here was {result.correctChoice}.
                    </div>
                  )}
                </div>
              </div>
            )}

            <CopyScoreDisplay
              score={score}
              isNew
              isDark={isDark}
              extraScores={result.scores}
              feedback={result.feedback}
            />

            {currentIdx < AB_ARENA_EXERCISES.length - 1 && (
              <button
                onClick={nextExercise}
                style={{
                  width: '100%', marginTop: 20, padding: '14px 24px', borderRadius: 12,
                  background: ACCENT, color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                  boxShadow: `0 4px 20px -4px rgba(124,58,237,0.45)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                Next Exercise <ChevronRight size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default ABArena;
